
import { SimulationConfig, Ball, Vector2D, MorphData, CollisionEvent, BallPiece } from '../../types';
import { ShapeUtils } from '../physics/ShapeUtils';
import { AUDIO } from '../../services/AudioService';
import { MS_PER_TICK } from '../../constants';
import { BallBreakAnimation } from '../animations/BallBreakAnimation';
import { RhythmicPhysics } from './RhythmicPhysics';

export const RhythmicLogic = {
  update: (
    b: Ball,
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    centerX: number,
    centerY: number,
    containerRadiusRef: React.MutableRefObject<number>,
    ballGrowthRateRef: React.MutableRefObject<number>,
    rhythmicTimeScaleRef: React.MutableRefObject<number>,
    rhythmicTimeScaleRateRef: React.MutableRefObject<number>,
    rotRad: number,
    hue: number,
    morphData: MorphData,
    collisionEventsRef: React.MutableRefObject<CollisionEvent[]>,
    persistentSpectrumBoostRef: React.MutableRefObject<Float32Array>,
    persistentSpectrumTargetRef: React.MutableRefObject<Float32Array>,
    now: number,
    currentTS: number,
    onShake: (intensity: number) => void,
    triggerBlast: (b: {pos: Vector2D, color: string, radius: number}, cmr?: number, gapAngle?: number, morph?: MorphData, animType?: string) => void,
    rhythmicBreakPiecesRef: React.MutableRefObject<BallPiece[]>,
    rhythmicResetTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    rhythmicIndexRef: React.MutableRefObject<number>,
    initSimulation: (resetRounds?: boolean) => void,
    onBounce?: () => void,
    onPlaylistAdvance?: () => void,
    trackOverrides?: { filtered?: number[], background?: number[], muted?: number[] }
  ) => {
    if (b.isBreaking) return;

    const substeps = 4;
    const dt = deltaTime / substeps;
    const g = effectiveCfg.gravity;

    // Sync visual properties
    const useRGB = effectiveCfg.rgbLighting && effectiveCfg.useBallRGB;
    b.color = useRGB ? `hsl(${hue}, 100%, 50%)` : effectiveCfg.ball1Color;
    b.style = effectiveCfg.ballStyle;
    b.shape = effectiveCfg.ballShape;
    if (effectiveCfg.ballRotationMode === 'SPIN') {
        b.angle = (b.angle || 0) + (effectiveCfg.ballRotationSpeed || 0.1) * (deltaTime / 1000);
    } else if (effectiveCfg.ballRotationMode === 'VELOCITY') {
        b.angle = Math.atan2(b.vel.y, b.vel.x);
    }

    if (effectiveCfg.ballWeightGrowthEnabled) {
        const spawnTime = b.spawnTime || now;
        const elapsed = (now - spawnTime) / 1000;
        const progress = Math.min(elapsed / (effectiveCfg.ballWeightGrowthTime || 60), 1.0);
        b.mass = (effectiveCfg.ballMinWeight || 1.0) + ((effectiveCfg.ballMaxWeight || 10.0) - (effectiveCfg.ballMinWeight || 1.0)) * progress;
    } else { b.mass = effectiveCfg.ballMass || 1.0; }

    if (effectiveCfg.ballGrowthMode === 'NONE') b.radius = effectiveCfg.ballRadius;

    const minContainerR = ShapeUtils.getMinRadius(morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadiusRef.current, effectiveCfg.containerRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
    const maxAllowed = Math.min(effectiveCfg.ballMaxRadius, minContainerR);

    const triggerBreak = () => {
        if (effectiveCfg.rhythmicMaxSizeSoundType === 'CUSTOM' && effectiveCfg.rhythmicMaxSizeCustomSoundId) {
            const userSound = effectiveCfg.userSounds?.find(s => s.id === effectiveCfg.rhythmicMaxSizeCustomSoundId);
            if (userSound?.url || userSound?.base64) AUDIO.playCustomBuffer(userSound.url || userSound.base64!, 1.0, effectiveCfg, true, true);
        } else AUDIO.playSoundEffect(effectiveCfg.rhythmicMaxSizeSound, effectiveCfg, 1.0, true);
        b.isBreaking = true; b.breakStartTime = Date.now();
        triggerBlast({ pos: { x: centerX, y: centerY }, color: b.color, radius: minContainerR }, minContainerR, 0, morphData, effectiveCfg.rhythmicOuterLayerAnimation || 'EXPLOSION');
        if (effectiveCfg.rhythmicBreakEffect !== 'NONE') {
            rhythmicBreakPiecesRef.current = BallBreakAnimation.createPieces(b.pos, b.radius, b.color, effectiveCfg.rhythmicBreakPieceCount, b.shape || 'CIRCLE', Date.now(), effectiveCfg.rhythmicBreakDuration);
        }
        rhythmicResetTimeoutRef.current = setTimeout(() => { initSimulation(); rhythmicResetTimeoutRef.current = null; }, effectiveCfg.rhythmicBreakResetDelay);
    };

    if (effectiveCfg.ballGrowthMode === 'TIME') {
        const nextRadius = b.radius + ballGrowthRateRef.current * (deltaTime / 1000);
        if (b.radius < maxAllowed && nextRadius >= maxAllowed) triggerBreak();
        b.radius = Math.max(b.radius, Math.min(maxAllowed, nextRadius));
        if (effectiveCfg.ballGrowthAcceleration !== 0) ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration * (deltaTime / 1000);
    } else if (effectiveCfg.ballGrowthMode === 'PLAYLIST_SYNC') {
        const totalDuration = AUDIO.getPlaylistTotalDuration(effectiveCfg.playlist, effectiveCfg);
        const elapsed = (Date.now() - (b.spawnTime || Date.now())) / 1000;
        const nextRadius = effectiveCfg.ballRadius + (maxAllowed - effectiveCfg.ballRadius) * (totalDuration > 0 ? Math.min(elapsed / (totalDuration / 1000), 1.0) : 1.0);
        if (b.radius < maxAllowed && nextRadius >= maxAllowed) triggerBreak();
        b.radius = nextRadius;
    }
    if (effectiveCfg.ballShrinkMode === 'TIME') b.radius = Math.max(effectiveCfg.ballMinRadius, b.radius - (effectiveCfg.ballShrinkRate * (deltaTime / 1000)));

    const melodySpeed = rhythmicTimeScaleRef.current;
    const visualTS = effectiveCfg.rhythmicBallSpeedDecoupled ? 1.0 : melodySpeed;

    for(let s=0; s<substeps; s++) {
        if (effectiveCfg.rhythmicSyncMode !== 'CURVED_PATH') b.vel.y += g * (dt / MS_PER_TICK) * visualTS;

        if (effectiveCfg.rhythmicSyncMode === 'CURVED_PATH' && b.lastBounceTime !== undefined && b.nextBounceDuration !== undefined) {
            const t = (now - b.lastBounceTime) / 1000 + (s * dt / 1000);
            if (t < b.nextBounceDuration) {
                const dist = b.curveBaseSpeed! * (t + ((effectiveCfg.rhythmicCurveSpeedProfile || 0) * b.nextBounceDuration / Math.PI) * (1 - Math.cos(Math.PI * t / b.nextBounceDuration)));
                const currentPhi = (b.originalAngle || 0) + (b.curvature || 0) * dist;
                const speed_tick = (b.curveBaseSpeed! * (1 + (effectiveCfg.rhythmicCurveSpeedProfile || 0) * Math.sin(Math.PI * t / b.nextBounceDuration))) * (MS_PER_TICK / 1000);
                b.vel.x = (speed_tick / visualTS) * Math.cos(currentPhi); b.vel.y = (speed_tick / visualTS) * Math.sin(currentPhi);
            }
        } else if (b.curvature && b.curvature !== 0) {
            const speed = Math.sqrt(b.vel.x*b.vel.x + b.vel.y*b.vel.y);
            if (speed > 0) {
                const acc = speed * speed * b.curvature;
                b.vel.x += (-b.vel.y / speed) * acc * (dt / MS_PER_TICK) * visualTS;
                b.vel.y += (b.vel.x / speed) * acc * (dt / MS_PER_TICK) * visualTS;
            }
        }
        
        const oldVel = { ...b.vel };
        b.pos.x += b.vel.x * (dt / MS_PER_TICK) * visualTS; b.pos.y += b.vel.y * (dt / MS_PER_TICK) * visualTS;
        const relX = b.pos.x - centerX, relY = b.pos.y - centerY;
        const angle = Math.atan2(relY, relX), dist = Math.sqrt(relX*relX + relY*relY);
        const lineWidth = effectiveCfg.containerLineWidth || 4, baseR = containerRadiusRef.current;
        const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData.from, morphData.to, morphData.factor, baseR, morphData.baseRadius) - lineWidth / 2;
        
        if (effectiveShapeR - b.radius > 0 && dist > effectiveShapeR - b.radius + 2) {
            b.pos.x = centerX + (relX / (dist || 1)) * (effectiveShapeR - b.radius - 0.5);
            b.pos.y = centerY + (relY / (dist || 1)) * (effectiveShapeR - b.radius - 0.5);
        }
        
        if (dist + b.radius >= effectiveShapeR && (b.vel.x * relX + b.vel.y * relY > 0 || (b.vel.x === 0 && b.vel.y === 0))) { 
            const speed = Math.sqrt(b.vel.x*b.vel.x + b.vel.y*b.vel.y) * visualTS;
            const timeSinceCollisionMs = ((dist + b.radius - effectiveShapeR) / Math.max(speed, 0.001)) * MS_PER_TICK;
            const exactCollisionTime = AUDIO.initCtx().currentTime - (timeSinceCollisionMs / 1000) + (effectiveCfg.rhythmicSyncOffset / 1000);

            const localNormal = ShapeUtils.getNormal({ x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad), y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad) }, morphData.from, baseR - lineWidth / 2, morphData.to, morphData.factor, morphData.baseRadius);
            const worldNormal = { x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad), y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad) };
            
            b.pos.x -= worldNormal.x * (dist + b.radius - effectiveShapeR + 2.0); b.pos.y -= worldNormal.y * (dist + b.radius - effectiveShapeR + 2.0);

            const step = AUDIO.getNextRhythmicStep(effectiveCfg.selectedMelody, effectiveCfg, rhythmicIndexRef.current, trackOverrides);
            AUDIO.playRhythmicStep(step.notesToPlay, effectiveCfg, exactCollisionTime, melodySpeed);
            rhythmicIndexRef.current = step.nextIndex;
            if (step.playlistAdvance && onPlaylistAdvance) onPlaylistAdvance();
            if (onBounce) onBounce();
            
            collisionEventsRef.current.push({ id: Math.random().toString(), pos: { x: b.pos.x + worldNormal.x * b.radius, y: b.pos.y + worldNormal.y * b.radius }, startTime: now - timeSinceCollisionMs, radius: b.radius * 2.5 * (effectiveCfg.collisionEffectScale || 1.0), color: b.color, effectType: effectiveCfg.collisionEffect, duration: effectiveCfg.collisionEffectDuration || 800 });

            if (effectiveCfg.rhythmicSpectrumEnabled) {
                const boostIndex = Math.floor(((Math.atan2(b.pos.y-centerY + worldNormal.y*b.radius, b.pos.x-centerX + worldNormal.x*b.radius) + Math.PI) / (2 * Math.PI)) * 256);
                const spread = effectiveCfg.rhythmicSpectrumCollisionSpread ?? 12;
                for (let i = -spread; i <= spread; i++) {
                    persistentSpectrumTargetRef.current[(boostIndex + i + 256) % 256] = Math.max(persistentSpectrumTargetRef.current[(boostIndex + i + 256) % 256], 1.8 * (effectiveCfg.rhythmicSpectrumCollisionIntensity ?? 1.0) * (1 - Math.abs(i) / (spread + 1)));
                }
            }
            onShake(5);
            if (effectiveCfg.rhythmicTimeScaleIncreaseMode === 'COLLISION') {
                rhythmicTimeScaleRef.current += rhythmicTimeScaleRateRef.current;
                if (effectiveCfg.rhythmicTimeScaleAcceleration !== 0) rhythmicTimeScaleRateRef.current += effectiveCfg.rhythmicTimeScaleAcceleration;
            }

            if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                const growth = ballGrowthRateRef.current;
                if (b.radius < maxAllowed && b.radius + growth >= maxAllowed) { triggerBreak(); return; }
                if (effectiveCfg.ballGrowthAcceleration !== 0) ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration;
                const actualGrowth = Math.max(0, Math.min(maxAllowed, b.radius + growth) - b.radius);
                b.radius += actualGrowth;
                b.pos.x -= worldNormal.x * (actualGrowth + 0.1); b.pos.y -= worldNormal.y * (actualGrowth + 0.1);
            }
            if (effectiveCfg.ballShrinkMode === 'COLLISION') b.radius = Math.max(effectiveCfg.ballMinRadius, b.radius - (effectiveCfg.ballShrinkRate || 0));
            
            const remainingTimeSec = Math.max(0.005, (Math.max(10, step.totalDuration) / Math.max(0.01, melodySpeed)) / 1000 - (timeSinceCollisionMs / 1000));
            const T = Math.max(0.1, remainingTimeSec / (MS_PER_TICK / 1000));
            
            let predictedRadius = b.radius;
            if (effectiveCfg.ballGrowthMode === 'TIME') predictedRadius += ballGrowthRateRef.current * remainingTimeSec + 0.5 * (effectiveCfg.ballGrowthAcceleration || 0) * remainingTimeSec * (remainingTimeSec - deltaTime / 1000);
            if (effectiveCfg.ballShrinkMode === 'TIME') predictedRadius -= (effectiveCfg.ballShrinkRate || 0) * remainingTimeSec;
            predictedRadius = Math.max(effectiveCfg.ballMinRadius, Math.min(maxAllowed, predictedRadius));

            if ((effectiveCfg.rhythmicSyncMode || 'RADIUS_AND_SPEED') === 'RADIUS_AND_SPEED') {
                const newR = Math.max(30, Math.min(effectiveCfg.containerRadius * (Math.max(10, step.totalDuration) / 500) * (effectiveCfg.rhythmicCircleScale || 1.0) * ((visualTS / melodySpeed) * (effectiveCfg.rhythmicBallSpeedScale || 1.0)), window.innerHeight/2 - 20));
                containerRadiusRef.current = newR;
            }

            RhythmicPhysics.calculateNextBounce(b, effectiveCfg, centerX, centerY, containerRadiusRef.current, predictedRadius, morphData, rotRad, visualTS, melodySpeed, Math.max(10, step.totalDuration), T, timeSinceCollisionMs, now, g, substeps, oldVel, worldNormal);
        }
    }
  },

  init: (
    effectiveCfg: SimulationConfig,
    rhythmicIndexRef: React.MutableRefObject<number>,
    createBall: (overrides: Partial<Ball>) => Ball,
    centerX: number,
    centerY: number,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } }
  ): Ball[] => {
    rhythmicIndexRef.current = 0;
    const total = Math.max(1, effectiveCfg.initialBalls || 1);
    const now = Date.now();
    return Array.from({ length: total }).map((_, i) => {
        const { pos, vel } = getInitialBallState(i, total, effectiveCfg.ballPlacementIndividualEnabled ? effectiveCfg.ballPlacementConfigs?.[i] : null);
        return createBall({ id: `rhythm-ball-${i}`, pos: { x: pos.x + centerX, y: pos.y + centerY }, vel, color: (effectiveCfg.ballPlacementIndividualEnabled ? effectiveCfg.ballPlacementConfigs?.[i]?.color : null) || (i === 0 ? effectiveCfg.ball1Color : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')), mass: effectiveCfg.ballMass || 1, spawnTime: now, curvature: 0, lastBounceTime: undefined, nextBounceDuration: undefined, curveBaseSpeed: undefined, originalAngle: undefined });
    });
  }
};
